import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Nav, Form, Card, Button, InputGroup, Badge } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { FaRegEnvelope } from "react-icons/fa";
import AddressForm from '../../components/Form/AddressForm';
import PhoneInput from '../../components/Form/PhoneInput';
import DaySchedule from './DaySchedule';
import ScheduleRuleDrawer from './ScheduleRuleDrawer';
import ExtrasList from './ExtrasList';
import WeekendExtrasSection from './WeekendExtrasSection';
import { ensureWeekendExtras } from './weekendExtras';
import { EXTRA_OWNERS, buildExtraOwnershipIndex, normalizeExtraKey } from './extraOwnership';
import API from '../../api/axios';
import { useParams } from 'react-router-dom';
import useCompanyContext from '../../hooks/useCompanyContext';
import '../../assets/styles/ShopForm.css';
import { useFormat } from '../../components/FormatContext';
import ShopSeasonDrawer from "./ShopSeasonDrawer"; // ✅ 新 Drawer
/* ======================= 适配器 & 常量 ======================= */

// 支付标签（兼容大小写键）
const paymentLabels = {
  AMEX: "American Express",
  VISA: "Visa",
  MASTERCARD: "Mastercard",
  CASH: "Cash (In-Person Only)",
  DEBIT: "Debit Cards",
  amex: "American Express",
  visa: "Visa",
  mastercard: "Mastercard",
  cash: "Cash (In-Person Only)",
  debitcard: "Debit Cards",
};


const omitKeys = (value = {}, keys = []) => {
  const next = { ...value };
  keys.forEach((key) => delete next[key]);
  return next;
};

const buildManagedShopExtraRow = (catalogItem, existingRow = null) => {
  const key = normalizeExtraKey(catalogItem.key);
  const isThirdPartyInsurance =
    key === 'third-party-insurance' ||
    String(catalogItem.id) ===
    'c02201ba-9404-42de-9043-ec39c83bf8a2';

  return {
    ...(existingRow?.id ? { id: existingRow.id } : {}),
    extraId: String(catalogItem.id),
    key,
    name: catalogItem.name || '',
    description: '',
    priceType: 'PER_DAY',
    pricePerDay: 0,
    maxPrice: null,
    isRequired: isThirdPartyInsurance,
  };
};

const ensureManagedShopExtras = (serverList = [], catalog = []) => {
  const rows = Array.isArray(serverList) ? serverList : [];
  const catalogList = Array.isArray(catalog) ? catalog : [];

  const existingByExtraId = new Map(
    rows
      .filter((item) => item?.extraId)
      .map((item) => [String(item.extraId), item])
  );

  const ownershipIndex = buildExtraOwnershipIndex(catalogList);
  const managedRows = catalogList
    .filter(item => ownershipIndex.getOwner(item) === EXTRA_OWNERS.MOTORCYCLE)
    .map((catalogItem) =>
      buildManagedShopExtraRow(
        catalogItem,
        existingByExtraId.get(String(catalogItem.id))
      )
    );

  const regularRows = rows.filter(
    item => ownershipIndex.getOwner(item) !== EXTRA_OWNERS.MOTORCYCLE
  );

  return ensureWeekendExtras([...regularRows, ...managedRows], catalogList, ownershipIndex);
};

/* ======================= 校验规则 ======================= */

// 基础信息验证（示例）
const detailSchema = yup.object().shape({
  shop: yup.object().shape({
    name: yup.string().required('Required'),
    address: yup.string().required('Required'),
    locationId: yup
      .string()
      .nullable()
      .test('location-or-place', 'Please select an address from suggestions', function (value) {
        return !!(value || this.parent?.placeId);
      }),
    placeId: yup.string().nullable(),
    phone: yup.string().required('Required'),
    email: yup.string().email('Invalid email').required('Required'),
    managementFee: yup.number().required('Required').typeError('Must be a number'),
    minDay: yup.number().required('Required').typeError('Must be a number'),
    daysBeforeRent: yup.number().required('Required').typeError('Must be a number'),
  })
});
const scheduleSchema = yup.object().shape({ schedule: yup.object().shape({}) });
const seasonSchema = yup.object().shape({ schedule: yup.object().shape({}) });
const extrasSchema = yup.object().shape({});

const SHOP_TABS = [
  { id: 'detail', label: 'Shop Details' },
  { id: 'schedule', label: 'Operating Schedule' },
  { id: 'seasons', label: 'Pricing Settings' },
  { id: 'extras', label: 'Services & Policies' },
];

const SCHEMA_MAP = {
  detail: detailSchema,
  schedule: scheduleSchema,
  seasons: seasonSchema,
  extras: extrasSchema,
};
const DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // UI Mon→Sun
const DEFAULT_DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const mapShopExtrasFromServer = (serverList = [], catalog = []) => {
  const nameById = new Map(catalog.map(x => [x.id, x.name || '']));
  const descById = new Map(catalog.map(x => [x.id, x.description || '']));
  const keyById = new Map(catalog.map(x => [x.id, x.key || '']));
  return serverList.map(se => ({
    id: se.id,                             // 可能为 undefined（新建）
    extraId: se.extraId,                   // 目录 id
    key: keyById.get(se.extraId) || '',
    name: nameById.get(se.extraId) || '',  // 仅展示用
    description: se.description ?? descById.get(se.extraId) ?? '',
    priceType: se.priceType || 'PER_DAY',  // PER_DAY | ONE_TIME
    pricePerDay: se.pricePerDay ?? 0,
    maxPrice: se.maxPrice,
    isRequired: !!se.isRequired,
  }));
};

const mapShopExtrasToServer = (uiList = []) => {
  const toNullableNumber = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  const toNumberOrZero = (v) => {
    if (v === '' || v === null || v === undefined) return 0;
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
  };

  return uiList.map(row => ({
    id: row.id || undefined,
    extraId: row.extraId,
    priceType: row.priceType,
    pricePerDay: toNumberOrZero(row.pricePerDay),
    maxPrice: row.priceType === 'PER_DAY' ? toNullableNumber(row.maxPrice) : null,
    isRequired: !!row.isRequired,
    description: row.description || '',
  }));
};

const makeDefaultSchedules = () =>
  DEFAULT_DAY_NAMES.map((_, index) => ({
    dayOfWeek: index,
    isClosed: true,
    morningOpen: undefined,
    morningClose: undefined,
    afternoonOpen: undefined,
    afternoonClose: undefined,
  }));

const normalizeWeek = (week = [], { withName = false } = {}) => {
  const mapByDow = new Map((Array.isArray(week) ? week : []).map(d => [d.dayOfWeek, d]));
  return DISPLAY_ORDER.map(dow => {
    const base = mapByDow.get(dow);
    const v = {
      dayOfWeek: dow,
      isClosed: base?.isClosed ?? true,
      morningOpen: base?.morningOpen,
      morningClose: base?.morningClose,
      afternoonOpen: base?.afternoonOpen,
      afternoonClose: base?.afternoonClose,
    };
    return withName ? { ...v, name: DEFAULT_DAY_NAMES[dow] } : v;
  });
};

// 去掉 UI-only 字段（如 name），并保证后端顺序
const denormalizeWeek = (week = []) =>
  [...(Array.isArray(week) ? week : [])]
    .map(({ name, ...rest }) => rest)
    .sort((a, b) => a.dayOfWeek - b.dayOfWeek);

/** ---------- header: Repeats yearly/Specific dates + 日期区间 + 操作 ---------- */
const RuleHeader = ({ rule, onEdit, onDelete, subtitle }) => {
  const fmt = (d, opts) => new Date(d).toLocaleDateString(undefined, opts);

  const rangeText = rule.isRecurring
    ? `${fmt(rule.startDate, { month: 'short', day: '2-digit' })} – ${fmt(rule.endDate, { month: 'short', day: '2-digit' })}`
    : `${fmt(rule.startDate)} – ${fmt(rule.endDate)}`;
  return (
    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
      {/* Left */}
      <div className="d-flex flex-column gap-1">
        <div className="d-flex align-items-center flex-wrap gap-2">
          <Badge
            className={`rounded-pill px-2 py-1 ${rule.isRecurring ? "bg-success-subtle text-success border border-success-subtle" : "bg-light text-muted"
              }`}
          >
            {rule.isRecurring ? "Repeats yearly" : "Specific dates"}
          </Badge>

          <div className="d-flex align-items-center text-nowrap">
            <i className="bi bi-calendar-range me-2 text-muted" />
            <span className="fw-semibold">{rangeText}</span>
          </div>
        </div>

        {/* Subtitle line (human-friendly description) */}
        {subtitle && (
          <div className="text-muted small">
            {subtitle}
          </div>
        )}
      </div>

      {/* Right actions */}
      <div className="d-flex gap-2">
        <Button variant="outline-primary" size="sm" onClick={onEdit}>
          Edit
        </Button>
        <Button variant="outline-danger" size="sm" onClick={onDelete} disabled={!rule.id}>
          Remove
        </Button>
      </div>
    </div>
  );
};


/** ---------- 预览组件（你已有的 그대로） ---------- */
const DailyScheduleView = ({ day }) => {
  if (!day || day.isClosed) return <div className="text-secondary mt-2">Closed</div>;
  const pieces = [];
  if (day.morningOpen && day.morningClose) pieces.push(`${day.morningOpen}–${day.morningClose}`);
  if (day.afternoonOpen && day.afternoonClose) pieces.push(`${day.afternoonOpen}–${day.afternoonClose}`);
  return (
    <div className="mt-2 d-flex align-items-center flex-wrap gap-2">
      {pieces.length
        ? pieces.map((t, i) => <span key={i} className="time-badge">{t}</span>)
        : <span className="text-warning small">Not set</span>}
    </div>
  );
};

const WeeklyScheduleView = ({ week, dayNames = DEFAULT_DAY_NAMES }) => {
  const ordered = [...(week || [])].sort(
    (a, b) => DISPLAY_ORDER.indexOf(a.dayOfWeek) - DISPLAY_ORDER.indexOf(b.dayOfWeek)
  );
  return (
    <Row className="weekly-grid mt-2">
      {ordered.map(d => {
        const label = dayNames[d.dayOfWeek] || `Day ${d.dayOfWeek}`;
        const hasMorning = d.morningOpen && d.morningClose;
        const hasAfternoon = d.afternoonOpen && d.afternoonClose;
        return (
          <Col xs={12} key={d.dayOfWeek} className="day-row mb-2 d-flex align-items-center flex-nowrap">
            <Col xs={3} className="day-name fw-semibold me-3" style={{ minWidth: 80 }}>{label}</Col>
            <Col xs={9} className="time-slots d-flex align-items-center flex-wrap gap-2">
              {d.isClosed
                ? <span className="text-danger small">Closed</span>
                : (hasMorning || hasAfternoon)
                  ? (
                    <>
                      {hasMorning && <span className="time-badge">{d.morningOpen}–{d.morningClose}</span>}
                      {hasAfternoon && <span className="time-badge">{d.afternoonOpen}–{d.afternoonClose}</span>}
                    </>
                  )
                  : <span className="text-warning small">Not set</span>
              }
            </Col>
          </Col>
        );
      })}
    </Row>
  );
};

/** ---------- 通过内容推断：是否属于 Closed Days ---------- */
const makeIsClosedRule = (normalizeWeek) => (rule) => {
  if (Array.isArray(rule?.weeklySchedule) && rule.weeklySchedule.length) {
    const week = normalizeWeek(rule.weeklySchedule);
    return week.every(d => d.isClosed);
  }
  if (rule?.dailySchedule) return !!rule.dailySchedule.isClosed;
  return false;
};


const ShopForm = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const {
    currentCompanyID,
    hasSpecificCompany,
  } = useCompanyContext();

  const supplierId = hasSpecificCompany
    ? String(currentCompanyID)
    : '';

  const [activeTab, setActiveTab] = useState('detail');
  const [loading, setLoading] = useState(isEditMode);
  const [extrasCatalog, setExtrasCatalog] = useState({ extras: [] });
  const [physicalShops, setPhysicalShops] = useState([]);
  const [formInit, setFormInit] = useState({});

  const currentIndex = SHOP_TABS.findIndex(
    (tab) => tab.id === activeTab
  );

  const loadShopForm = useCallback(async () => {
    if (isEditMode) {
      const { data } = await API.get(`/shop/${id}/form`);
      const values = data?.values ?? data?.defaults ?? {};
      const catalogList = data?.options?.extras || [];
      const rawExtras = values.extras || [];

      const schedules =
        Array.isArray(values.schedules) && values.schedules.length
          ? values.schedules
          : makeDefaultSchedules();

      return {
        formInit: {
          shop: values.shop ?? {},
          schedules,
          specialSchedules: values.specialSchedules ?? [],
          seasons: values.seasons ?? [],
          extras: mapShopExtrasFromServer(
            ensureManagedShopExtras(rawExtras, catalogList),
            catalogList
          ),
          taxes: values.taxes ?? {},
        },
        extrasCatalog: data?.options?.extras || { extras: [] },
        physicalShops: data?.options?.physicalShops || [],
      };
    }

    if (!supplierId) {
      throw new Error('A specific company is required to create a shop.');
    }

    const { data } = await API.post('/shop/form-meta', {
      supplierId,
    });

    const values = data?.values ?? data?.defaults ?? {};
    const catalogList = data?.options?.extras || [];
    const rawExtras = values.extras || [];

    return {
      formInit: {
        shop: values.shop ?? {},
        schedules: makeDefaultSchedules(),
        specialSchedules: [],
        seasons: [],
        extras: mapShopExtrasFromServer(
          ensureManagedShopExtras(rawExtras, catalogList),
          catalogList
        ),
        taxes: values.taxes ?? {},
      },
      extrasCatalog: data?.options?.extras || { extras: [] },
      physicalShops: data?.options?.physicalShops || [],
    };
  }, [id, isEditMode, supplierId]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const data = await loadShopForm();

        if (cancelled) return;

        setFormInit(data.formInit);
        setExtrasCatalog(data.extrasCatalog);
        setPhysicalShops(data.physicalShops);
      } catch (error) {
        if (!cancelled) {
          console.error(error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [loadShopForm]);

  const handleSubmit = useCallback(
    async (values, { setSubmitting }) => {
      const shopWithoutExtraFields = omitKeys(values.shop || {}, [
        'placeId',
        'isoCode',
        'slug',
        'pricingMode',
        'isTaxable',
      ]);

      const cleanedSeasons =
        values.seasons?.map((item) => omitKeys(item, ['id'])) || [];

      const cleanedSpecialSchedules =
        values.specialSchedules?.map((item) =>
          omitKeys(item, ['id', 'priority'])
        ) || [];

      const catalogList = Array.isArray(extrasCatalog)
        ? extrasCatalog
        : Array.isArray(extrasCatalog?.extras)
          ? extrasCatalog.extras
          : [];

      const ensuredExtras = ensureManagedShopExtras(
        values.extras || [],
        catalogList
      );

      const cleanedExtras = mapShopExtrasToServer(ensuredExtras).map(
        (item) => (item.id ? item : omitKeys(item, ['id']))
      );

      const cleanedSchedules =
        values.schedules?.map(
          ({
            dayOfWeek,
            isClosed,
            morningOpen,
            morningClose,
            afternoonOpen,
            afternoonClose,
          }) => ({
            dayOfWeek,
            isClosed,
            morningOpen,
            morningClose,
            afternoonOpen,
            afternoonClose,
          })
        ) || [];

      const payload = {
        ...values,
        shop: {
          ...shopWithoutExtraFields,
          extras: cleanedExtras,
        },
        seasons: cleanedSeasons,
        specialSchedules: cleanedSpecialSchedules,
        extras: cleanedExtras,
        schedules: cleanedSchedules,
      };

      try {
        if (isEditMode) {
          await API.patch(`/shop/${id}`, payload);
        } else {
          await API.post('/shop/both', payload);
        }

        alert('Data saved successfully!');
      } catch (error) {
        console.error(error);
        alert(error?.response?.data?.message || error?.message || 'Save failed.');
      } finally {
        setSubmitting(false);
      }
    },
    [extrasCatalog, id, isEditMode]
  );

  const handleTabChange = useCallback(
    async (tabId, formik) => {
      try {
        await SCHEMA_MAP[activeTab].validate(formik.values, {
          abortEarly: false,
        });

        setActiveTab(tabId);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (validationError) {
        const nextErrors = {};

        if (Array.isArray(validationError?.inner)) {
          validationError.inner.forEach((item) => {
            if (item.path && !nextErrors[item.path]) {
              nextErrors[item.path] = item.message;
            }
          });
        }

        formik.setErrors(nextErrors);
      }
    },
    [activeTab]
  );

  return (
    <Formik
      enableReinitialize
      initialValues={formInit}
      onSubmit={handleSubmit}
      validateOnChange={false}
      validateOnBlur={true}
    >
      {(formik) => (
        <Container fluid className="d-flex flex-column">
          <Row className="justify-content-center">
            <Col sm={12} className="mb-4">
              <span className="fw-semibold">Shop Information</span>
            </Col>

            <Col
              sm="auto"
              style={{
                position: 'sticky',
                top: '10px',
                height: 'fit-content',
                alignSelf: 'flex-start',
                maxWidth: '280px',
              }}
            >
              <Card>
                <Nav variant="pills" className="flex-column p-3">
                  {SHOP_TABS.map((tab) => (
                    <Nav.Item key={tab.id}>
                      <Nav.Link
                        active={activeTab === tab.id}
                        onClick={() => handleTabChange(tab.id, formik)}
                        className="mb-2 rounded"
                      >
                        {tab.label}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
              </Card>
            </Col>

            <Col className="flex-grow-1">
              <Card className="p-4">
                {activeTab === 'detail' && (
                  <DetailTab
                    formik={formik}
                    paymentLabels={paymentLabels}
                    physicalShops={physicalShops}
                  />
                )}

                {activeTab === 'schedule' && (
                  <ScheduleTab
                    extrasCatalog={extrasCatalog}
                    formik={formik}
                    normalizeWeek={normalizeWeek}
                    denormalizeWeek={denormalizeWeek}
                    sortWeekForDisplay={(week) =>
                      normalizeWeek(week, { withName: true })
                    }
                    makeDefaultSchedules={makeDefaultSchedules}
                  />
                )}

                {activeTab === 'seasons' && (
                  <SeasonTab formik={formik} />
                )}

                {activeTab === 'extras' && (
                  <ExtrasTab
                    formik={formik}
                    extrasCatalog={extrasCatalog}
                  />
                )}

                <div className="d-flex justify-content-end mt-3">
                  {currentIndex < SHOP_TABS.length - 1 ? (
                    <Button
                      type="button"
                      onClick={() =>
                        handleTabChange(
                          SHOP_TABS[currentIndex + 1].id,
                          formik
                        )
                      }
                      disabled={formik.isSubmitting || loading}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={formik.handleSubmit}
                      className="border ms-1 btn-custom-success"
                      disabled={formik.isSubmitting || loading}
                    >
                      {formik.isSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      )}
    </Formik>
  );
};

// detail 信息 Tab
const DetailTab = ({ formik, paymentLabels, physicalShops }) => (
  <FormSection title="Shop Detail">
    <Row>
      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>Shop Name <span className="text-danger">*</span></Form.Label>
          <Form.Control
            name="shop.name"
            value={formik.values.shop?.name || ""}
            onChange={formik.handleChange}
            isInvalid={!!formik.errors["shop.name"]}
          />
          <Form.Control.Feedback type="invalid">
            {formik.errors["shop.name"]}
          </Form.Control.Feedback>
        </Form.Group>
      </Col>
    </Row>

    <Row>
      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>Shop Type</Form.Label>
          <Form.Select
            name="shop.shopType"
            value={formik.values.shop?.shopType || "PHYSICAL"} // 'CITY' | 'DELIVERY'
            onChange={formik.handleChange}
          >
            <option value="PHYSICAL">Physical Store (Customer Pickup)</option>
            <option value="VIRTUAL">Delivery Point (Vehicle Transport)</option>
          </Form.Select>
        </Form.Group>
      </Col>

      <Col md={6}>
        {formik.values.shop?.shopType === "VIRTUAL" && (
          <Form.Group className="mb-3">
            <Form.Label>Original Physical Shop <span className="text-danger">*</span></Form.Label>
            <Form.Select
              name="shop.physicalShopId"
              value={formik.values.shop?.physicalShopId || ""}
              onChange={formik.handleChange}
            >
              <option value="" disabled>Select Shop</option>
              {Array.isArray(physicalShops) &&
                physicalShops.map((s) => {
                  const optionId = String(s.id);
                  const optionName = String(s.name || '').trim(); // 去掉尾部空格/制表符
                  return (
                    <option key={optionId} value={optionId}>
                      {optionName}
                    </option>
                  );
                })}
            </Form.Select>
            <Form.Text className="text-muted">
              * Select the physical store that will provide vehicles for this delivery point
            </Form.Text>
            <Form.Control.Feedback type="invalid">
              {formik.errors["shop.physicalShopId"]}
            </Form.Control.Feedback>
          </Form.Group>
        )}
      </Col>

    </Row>

    <Row>
      <Col md={12}>

        <AddressForm
          formik={formik}
          basePath="shop"
          errorInfo={formik.errors['shop.address']}
        // language="es"
        // countries="es,us"
        // token={process.env.REACT_APP_MAPBOX_TOKEN}
        />
      </Col>
    </Row>

    <Row>
      <Col md={6}>
        <PhoneInput
          formik={formik}
          label="Shop Phone"
          prefixField="shop.prefix"      // 如果没有前缀，PhoneInput 里做兼容
          phoneField="shop.phone"
          error={formik.errors["shop.phone"]}
        />
      </Col>

      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>Shop Email <span className="text-danger">*</span></Form.Label>
          <InputGroup>
            <InputGroup.Text><FaRegEnvelope /></InputGroup.Text>
            <Form.Control
              name="shop.email"
              value={formik.values.shop?.email || ""}
              onChange={formik.handleChange}
              isInvalid={!!formik.errors["shop.email"]}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors["shop.email"]}
            </Form.Control.Feedback>
          </InputGroup>
        </Form.Group>
      </Col>

      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>Minimum Rental Period (Days)</Form.Label>
          <Form.Control
            type="number"
            min="0"
            name="shop.minDay"
            value={formik.values.shop?.minDay ?? 0}
            onChange={formik.handleChange}
            isInvalid={!!formik.errors["shop.minDay"]}
          />
        </Form.Group>
      </Col>

      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>Minimum Days to Rent in Advance</Form.Label>
          <Form.Control
            type="number"
            min="0"
            name="shop.daysBeforeRent"
            value={formik.values.shop?.daysBeforeRent ?? 0}
            onChange={formik.handleChange}
            isInvalid={!!formik.errors["shop.daysBeforeRent"]}
          />
        </Form.Group>
      </Col>

      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>Request License Copy</Form.Label>
          <Form.Select
            name="shop.licenseRequest"
            value={formik.values.shop?.licenseRequest ? 'true' : 'false'}
            onChange={(e) => formik.setFieldValue('shop.licenseRequest', e.target.value === 'true')}
          >
            <option value="false">No</option>
            <option value="true">Yes</option>
          </Form.Select>
        </Form.Group>
      </Col>

      <Col md={6}>
        <Form.Group className="mb-4">
          <Form.Label>Platform Price Markup (%)</Form.Label>
          <InputGroup hasValidation>
            <Form.Control
              type="number"
              min="0"
              step={0.01}
              name="shop.managementFee"
              value={formik.values.shop?.managementFee ?? 0}
              onChange={formik.handleChange}
              isInvalid={!!formik.errors["shop.managementFee"]}
            />
            <InputGroup.Text>%</InputGroup.Text>
          </InputGroup>
        </Form.Group>
      </Col>

      {/* ✅ only show taxes when shop.isTaxable === true */}
      {formik.values.shop?.isTaxable && (
        <>
          <Col md={6}>
            <Form.Group className="mb-4">
              <Form.Label>Environmental Surcharge</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  step={0.01}
                  min="0"
                  name="taxes.envSurcharge"
                  value={formik.values.taxes?.envSurcharge ?? ""}
                  onChange={formik.handleChange}
                />
                <InputGroup.Text>%</InputGroup.Text>
              </InputGroup>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-4">
              <Form.Label>Concession Recovery Fee</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  step={0.01}
                  min="0"
                  name="taxes.concessionRecoveryFee"
                  value={formik.values.taxes?.concessionRecoveryFee ?? ""}
                  onChange={formik.handleChange}
                />
                <InputGroup.Text>%</InputGroup.Text>
              </InputGroup>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-4">
              <Form.Label>Mandatory Government Fee</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  step={0.01}
                  min="0"
                  name="taxes.mandatoryGovFee"
                  value={formik.values.taxes?.mandatoryGovFee ?? ""}
                  onChange={formik.handleChange}
                />
                <InputGroup.Text>%</InputGroup.Text>
              </InputGroup>
            </Form.Group>
          </Col>

        </>
      )}

      <Row>
        <Col md={6}>
          <Form.Group className="mb-4">
            <Form.Label>Vehicle Availability Check</Form.Label>
            <Form.Select
              name="shop.isAutoConfirm"
              value={formik.values.shop?.isAutoConfirm ? 'true' : 'false'}
              onChange={(e) => formik.setFieldValue('shop.isAutoConfirm', e.target.value === 'true')}
            >
              <option value="false">Manual Availability Verification</option>
              <option value="true">Auto-Confirm Availability</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-4">
            <Form.Label>Status</Form.Label>
            <Form.Select
              name="shop.isActive"
              value={formik.values.shop?.isActive ? 'true' : 'false'}
              onChange={(e) => formik.setFieldValue('shop.isActive', e.target.value === 'true')}
            >
              <option value="false">Inactive</option>
              <option value="true">Active</option>
            </Form.Select>
          </Form.Group>
        </Col>

      </Row>
      {/* 支付方式（租金） */}
      <Col md={6}>
        <Form.Group className="mb-4">
          <Form.Label>Payment Methods for Rentals</Form.Label>
          {Object.keys(formik.values.shop?.depositMethod || {}).map((k) => (
            <Form.Check
              key={k}
              type="switch"
              id={`rental-${k}`}
              label={paymentLabels[k] || k}
              checked={!!formik.values.shop.depositMethod[k]}
              onChange={() =>
                formik.setFieldValue(`shop.depositMethod.${k}`, !formik.values.shop.depositMethod[k])
              }
              className="my-2"
            />
          ))}
        </Form.Group>
      </Col>

      {/* 押金支付方式 */}
      <Col md={6}>
        <Form.Group className="mb-4">
          <Form.Label>Payment Methods for Deposit</Form.Label>
          {Object.keys(formik.values.shop?.paymentMethod || {}).map((k) => (
            <Form.Check
              key={k}
              type="switch"
              id={`deposit-${k}`}
              label={paymentLabels[k] || k}
              checked={!!formik.values.shop.paymentMethod[k]}
              onChange={() =>
                formik.setFieldValue(`shop.paymentMethod.${k}`, !formik.values.shop.paymentMethod[k])
              }
              className="my-2"
            />
          ))}
        </Form.Group>
      </Col>
    </Row>
  </FormSection>
);

const ScheduleTab = ({ formik, extrasCatalog, normalizeWeek, denormalizeWeek, sortWeekForDisplay, makeDefaultSchedules, DAY_NAMES = DEFAULT_DAY_NAMES }) => {
  // 1) week：保证 7 天
  const week = Array.isArray(formik.values.schedules) && formik.values.schedules.length
    ? formik.values.schedules
    : makeDefaultSchedules();

  const days = normalizeWeek(week, { withName: true });

  const updateDay = (updated) => {
    const next = denormalizeWeek(
      days.map(d => (d.dayOfWeek === updated.dayOfWeek ? { ...updated } : d))
    );
    formik.setFieldValue('schedules', next, false);
  };

  const formatDaySlots = (d) => {
    if (!d || d.isClosed) return 'Closed';
    const parts = [];
    if (d.morningOpen && d.morningClose) parts.push(`${d.morningOpen}–${d.morningClose}`);
    if (d.afternoonOpen && d.afternoonClose) parts.push(`${d.afternoonOpen}–${d.afternoonClose}`);
    return parts.length ? parts.join(', ') : 'Not set';
  };

  const summarizeGroup = (groupDays) => {
    const texts = groupDays.map(formatDaySlots);
    const uniq = Array.from(new Set(texts));
    return uniq.length === 1 ? uniq[0] : 'Varies';
  };

  const getWeekdays = (days) => days.filter(d => [1, 2, 3, 4, 5].includes(d.dayOfWeek));
  const getWeekend = (days) => days.filter(d => [6, 0].includes(d.dayOfWeek));
  /**
 * 生成 Special hours 的摘要行：
 * - Daily（7天一致）
 * - Weekdays/Weekend（两组一致）
 * - Varies by day（不规则）
 */
  const summarizeWeeklySchedule = (normalizedWeek) => {
    const week = normalizedWeek || [];
    if (!week.length) {
      return { mode: 'empty', lines: [{ left: 'Schedule', right: 'Not set' }] };
    }

    const dayTexts = week.map(d => formatDaySlots(d));
    const uniqAll = Array.from(new Set(dayTexts));

    // 1) 全部一致 => Daily
    if (uniqAll.length === 1) {
      return {
        mode: 'daily',
        lines: [{ left: 'Daily', right: uniqAll[0] }],
      };
    }

    // 2) 工作日/周末各自一致 => Weekdays + Weekend
    const wd = summarizeGroup(getWeekdays(week));
    const we = summarizeGroup(getWeekend(week));

    if (wd !== 'Varies' && we !== 'Varies') {
      return {
        mode: 'split',
        lines: [
          { left: 'Weekdays (Mon–Fri)', right: wd },
          { left: 'Weekend (Sat–Sun)', right: we },
        ],
      };
    }

    // 3) 不规则
    return {
      mode: 'varies',
      lines: [{ left: 'Schedule', right: 'Varies by day' }],
    };
  };
  const formatRuleRangeText = (rule) => {
    const fmt = (d, opts) => new Date(d).toLocaleDateString(undefined, opts);
    return rule.isRecurring
      ? `${fmt(rule.startDate, { month: 'short', day: '2-digit' })} – ${fmt(rule.endDate, { month: 'short', day: '2-digit' })}`
      : `${fmt(rule.startDate)} – ${fmt(rule.endDate)}`;
  };

  // 2) specialSchedules 同步

  const [specialRules, setSpecialRules] = useState(
    Array.isArray(formik.values.specialSchedules) ? formik.values.specialSchedules : []
  );
  useEffect(() => {
    setSpecialRules(Array.isArray(formik.values.specialSchedules) ? formik.values.specialSchedules : []);
  }, [formik.values.specialSchedules]);

  // 3) Drawer state
  const [showDrawer, setShowDrawer] = useState(false);
  const [drawerType, setDrawerType] = useState('closed'); // 'closed' | 'modified'
  const [editingRule, setEditingRule] = useState(null);

  const handleSave = (data) => {
    const list = Array.isArray(specialRules) ? [...specialRules] : [];

    // 统一 week 结构
    const normalizedWeek = Array.isArray(data.weeklySchedule)
      ? denormalizeWeek(normalizeWeek(data.weeklySchedule))
      : undefined;

    const toSave = { ...data, weeklySchedule: normalizedWeek };

    const idx = list.findIndex(r => r.id === toSave.id);
    const next = idx === -1
      ? [...list, { ...toSave, id: toSave.id ?? Date.now() }]
      : list.map(r => (r.id === toSave.id ? toSave : r));

    setSpecialRules(next);
    formik.setFieldValue('specialSchedules', next, false);

    setShowDrawer(false);
    setEditingRule(null);
  };

  const handleDelete = (id) => {
    const next = (specialRules || []).filter(r => r.id !== id);
    setSpecialRules(next);
    formik.setFieldValue('specialSchedules', next, false);
  };

  const copyFromMondayToAll = () => {
    const mon = days.find(d => d.dayOfWeek === 1);
    if (!mon) return;
    const next = days.map(d =>
      d.dayOfWeek === 1
        ? d
        : {
          dayOfWeek: d.dayOfWeek,
          isClosed: !!mon.isClosed,
          morningOpen: mon.morningOpen,
          morningClose: mon.morningClose,
          afternoonOpen: mon.afternoonOpen,
          afternoonClose: mon.afternoonClose,
        }
    )
      .map(({ name, ...rest }) => rest)
      .sort((a, b) => a.dayOfWeek - b.dayOfWeek);
    formik.setFieldValue('schedules', next, false);
  };

  const isClosedRule = useMemo(() => makeIsClosedRule(normalizeWeek), [normalizeWeek]);

  const closedRules = useMemo(
    () => (specialRules || []).filter(isClosedRule),
    [specialRules, isClosedRule]
  );
  const modifiedRules = useMemo(
    () => (specialRules || []).filter(r => !isClosedRule(r)),
    [specialRules, isClosedRule]
  );
  const sortedModifiedRules = useMemo(() => {
    const list = [...(modifiedRules || [])];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return list.sort((a, b) => {
      const aS = new Date(a.startDate);
      const bS = new Date(b.startDate);

      const aFuture = !isNaN(aS) && aS >= today;
      const bFuture = !isNaN(bS) && bS >= today;

      // 未来的排在前面
      if (aFuture !== bFuture) return aFuture ? -1 : 1;

      // 都是未来：越近越前
      if (aFuture && bFuture) return aS - bS;

      // 都是过去：越近的过去越前（也可以改成越早越前，看你习惯）
      return bS - aS;
    });
  }, [modifiedRules]);
  const SpecialHoursCard = ({
    rule,
    normalizeWeek,
    WeeklyScheduleView,
    DailyScheduleView,
    onEdit,
    onDelete,
  }) => {
    const [expanded, setExpanded] = React.useState(false);

    const rangeText = formatRuleRangeText(rule);
    const typeText = rule.isRecurring ? 'Repeats yearly' : 'Specific dates';

    // ✅ 推荐颜色：Repeats yearly 用 success（稳定），Specific 用 light/secondary
    const badgeClass = rule.isRecurring
      ? 'bg-success-subtle text-success border border-success-subtle'
      : 'bg-light text-muted border';

    // 统一拿到 week（用于摘要/详情）
    const week = Array.isArray(rule.weeklySchedule)
      ? normalizeWeek(rule.weeklySchedule)
      : null;

    const summary = week ? summarizeWeeklySchedule(week) : { mode: 'empty', lines: [{ left: 'Schedule', right: 'Not set' }] };

    return (
      <Card className="schedule-card mb-3 shadow-sm border-0">
        <Card.Body className="schedule-card py-3">

          {/* Header */}
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <div className="d-flex align-items-center flex-wrap gap-2">
                <Badge className={`rounded-pill px-2 py-1 ${badgeClass}`}>
                  {typeText}
                </Badge>

                <span className="fw-semibold">{rangeText}</span>
              </div>
            </div>

            <div className="d-flex gap-2">
              <Button variant="outline-primary" size="sm" onClick={onEdit}>Edit</Button>
              <Button variant="outline-danger" size="sm" onClick={onDelete} disabled={!rule.id}>Remove</Button>
            </div>
          </div>

          {/* Summary (always visible) */}
          <div className="mt-3 pt-3 border-top">
            <div className="summary-grid">
              {summary.lines.map((line, idx) => (
                <React.Fragment key={idx}>
                  <div className="summary-label">{line.left}</div>
                  <div className="summary-value">{line.right}</div>
                </React.Fragment>
              ))}

              {(summary.mode === 'varies' || summary.mode === 'split' || summary.mode === 'daily') && (
                <div className="summary-actions">
                  <Button
                    variant="link"
                    className="p-0 text-decoration-none"
                    onClick={() => setExpanded(v => !v)}
                  >
                    {expanded ? 'Hide details' : 'View details'}
                  </Button>
                </div>
              )}
            </div>

            {expanded && (
              <div className="mt-2">
                {Array.isArray(rule.weeklySchedule)
                  ? <WeeklyScheduleView week={week} />
                  : <DailyScheduleView day={rule.dailySchedule} />
                }
              </div>
            )}
          </div>

        </Card.Body>
      </Card>
    );
  };

  return (
    <FormSection title="Operating Schedule">
      <Row>

        {/* 1) Default Hours */}
        {/* Default hours header */}
        <Col md={12} className="section-head">
          <div>
            <div className="section-title">Default hours</div>
            <div className="section-desc">Your standard weekly opening hours.</div>
          </div>
          <div className="section-action">
            <Button size="sm" variant="outline-primary" onClick={copyFromMondayToAll}>Copy Monday → all days</Button>
          </div>
        </Col>

        <Col md={12} className="mt-2 ">
          {[...days]
            .sort((a, b) => DISPLAY_ORDER.indexOf(a.dayOfWeek) - DISPLAY_ORDER.indexOf(b.dayOfWeek))
            .map(day => (
              <DaySchedule key={day.dayOfWeek} dayConfig={day} onChange={updateDay} />
            ))}
        </Col>

        <WeekendExtrasSection formik={formik} extrasCatalog={extrasCatalog} />

        {/* 2) Closed Days */}
        <Col md={12} className="section-head">
          <div>
            <div className="section-title">Closures</div>
            <div className="section-desc">Days the store is not open.</div>
          </div>
          <div className="section-action">
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                setEditingRule(null);
                setDrawerType('closed');
                setShowDrawer(true);
              }}
            >
              + Add closure
            </Button>
          </div>
        </Col>

        <Col md={12} className="mt-3">
          {closedRules.length === 0 ? (
            <div className="text-muted">No closed days.</div>
          ) : (
            closedRules.map(rule => (
              <Card key={rule.id ?? rule.startDate + rule.endDate} className="schedule-card mb-3 shadow-sm border-0">
                <Card.Body className="py-3">
                  <RuleHeader
                    rule={rule}
                    onEdit={() => { setEditingRule(rule); setDrawerType('closed'); setShowDrawer(true); }}
                    onDelete={() => handleDelete(rule.id)}
                  />
                  <div className="status-row">
                    <Badge className="bg-danger-subtle text-danger border border-danger-subtle rounded-pill px-2 py-1">
                      Closed
                    </Badge>
                    <span className="text-muted small">{rule.isRecurring ? 'Closed every year during this period.' : 'Closed for this date range.'}</span>
                  </div>
                </Card.Body>
              </Card>
            ))
          )}
        </Col>

        {/* temporary hours */}
        <Col md={12} className="mt-4 d-flex justify-content-between align-items-start">
          <div>
            <div className="section-title">Temporary hours</div>
            <div className="section-desc">Override your default hours during specific dates.</div>
          </div>
          <div className="section-action">
            <Button
              size="sm"
              variant="primary"
              onClick={() => { setEditingRule(null); setDrawerType('modified'); setShowDrawer(true); }}
            >
              + Add temporary hours
            </Button>
          </div>
        </Col>

        <Col md={12} className="mt-3">
          {sortedModifiedRules.length === 0 ? (
            <Card className="schedule-card shadow-sm border-0">
              <Card.Body className="py-3">
                <div className="text-muted small mt-1">No temporary hours yet.</div>
              </Card.Body>
            </Card>
          ) : (
            sortedModifiedRules.map(rule => (
              <SpecialHoursCard
                key={rule.id ?? rule.startDate + rule.endDate}
                rule={rule}
                normalizeWeek={normalizeWeek}
                WeeklyScheduleView={WeeklyScheduleView}
                DailyScheduleView={DailyScheduleView}
                onEdit={() => { setEditingRule(rule); setDrawerType('modified'); setShowDrawer(true); }}
                onDelete={() => handleDelete(rule.id)}
              />
            ))
          )}
        </Col>

        {/* Drawer */}
        <ScheduleRuleDrawer
          show={showDrawer}
          type={drawerType}
          initialData={editingRule}
          onClose={() => { setShowDrawer(false); setEditingRule(null); }}
          onSubmit={handleSave}
          normalizeWeek={normalizeWeek}
          denormalizeWeek={denormalizeWeek}
          sortWeekForDisplay={sortWeekForDisplay}
          dayNames={DAY_NAMES}
        />

      </Row>
    </FormSection>
  );
};

// Season Tab
const SeasonTab = ({ formik }) => {
  const { formatCurrency } = useFormat();
  const [showDrawer, setShowDrawer] = useState(false);     // ✅
  const [seasons, setSeasons] = useState(Array.isArray(formik.values.seasons) ? formik.values.seasons : []);
  const [editingSeason, setEditingSeason] = useState(null);

  const [drawerMode, setDrawerMode] = useState("PERCENTAGE"); // 'SEASON' | 'PERCENTAGE' | 'FIXED'

  useEffect(() => {
    setSeasons(Array.isArray(formik.values.seasons) ? formik.values.seasons : []);
  }, [formik.values.seasons]);

  const pricingMode = (formik.values?.shop?.pricingMode || "PERCENTAGE").toUpperCase(); // 'PERCENTAGE' | 'SEASON'
  const mainMode = pricingMode === "SEASON" ? "SEASON" : "PERCENTAGE";

  const parseNumeric = (v) => {
    if (v === null || v === undefined) return undefined;
    if (typeof v === "number") return Number.isFinite(v) ? v : undefined;
    const n = Number(String(v).replace(",", ".").trim());
    return Number.isFinite(n) ? n : undefined;
  };

  const renderSeasonBadge = (rule) => {
    const s = String(rule.season || "").toUpperCase();

    const valueCents = parseNumeric(rule.valueCents);

    // FIXED: cents -> EUR
    const valueForShow =
      s === "FIXED"
        ? (typeof valueCents === "number" ? valueCents / 100 : undefined)
        : valueCents;

    const showPercentage =
      s === "PERCENTAGE" && typeof valueForShow === "number";

    const showFixed =
      s === "FIXED" && typeof valueForShow === "number";

    const label =
      s === "PERCENTAGE"
        ? "Adjust product price"
        : s === "FIXED"
          ? "Fixed increase"
          : s === "HIGH"
            ? "High season"
            : "Mid season";

    return (
      <div className="d-flex align-items-center gap-2">
        <Badge bg="light" text="dark" className="py-2" style={{ minWidth: 140 }}>
          {label}
        </Badge>

        {showPercentage && (
          <span className="fw-semibold text-nowrap">
            {valueForShow > 0 ? "+" : ""}
            {valueForShow}%
            <span className="text-muted fw-normal ms-2">
              on product price
            </span>
          </span>
        )}

        {showFixed && (
          <span className="fw-semibold text-nowrap">
            {formatCurrency(valueForShow, "EUR")}
            <span className="text-muted fw-normal ms-2">
              per day
            </span>
          </span>
        )}

        {(s === "MEDIUM" || s === "HIGH") && (
          <span className="text-muted small text-nowrap">
            Date range only
          </span>
        )}
      </div>
    );
  };

  const confirmDelete = (id) => {
    if (!window.confirm("确定要删除这条规则吗？")) return;
    const next = (seasons || []).filter((s) => s.id !== id);
    setSeasons(next);
    formik.setFieldValue("seasons", next, false);
  };

  const handleSave = (data) => {
    const list = Array.isArray(seasons) ? [...seasons] : [];
    const withId = data.id ? data : { ...data, id: Date.now() };

    const idx = list.findIndex((r) => r.id === withId.id);
    const next = idx === -1 ? [...list, withId] : list.map((r) => (r.id === withId.id ? withId : r));

    setSeasons(next);
    formik.setFieldValue("seasons", next, false);

    setShowDrawer(false);
    setEditingSeason(null);
  };

  const fixedRules = useMemo(
    () => seasons.filter((r) => String(r.season || "").toUpperCase() === "FIXED"),
    [seasons]
  );

  const seasonRules = useMemo(() => {
    return seasons.filter((r) => {
      const s = String(r.season || "").toUpperCase();
      if (pricingMode === "SEASON") return s === "MEDIUM" || s === "HIGH";
      return s === "PERCENTAGE"; // default
    });
  }, [seasons, pricingMode]);

  const SeasonRuleCard = ({ rule }) => (
    <Card className="schedule-card mb-3 shadow-sm border-0">
      <Card.Body className="schedule-card py-3">
        <Row className="align-items-center">
          <Col xs={12} md={8} lg={9}>
            <div className="d-flex flex-wrap align-items-center gap-3">
              <Badge
                pill
                className={`rounded-pill px-2 py-1 ${rule.isRecurring ? "bg-success-subtle text-success border border-success-subtle" : "bg-light text-muted"}`}
                style={{ minWidth: 80 }}
              >
                {rule.isRecurring ? "Repeats yearly" : "Specific dates"}
              </Badge>

              <div className="d-flex align-items-center text-nowrap">
                <i className="bi bi-calendar-range me-2" />
                <span className="fw-medium" style={{ minWidth: 240 }}>
                  {rule.isRecurring ? (
                    <>
                      {new Date(rule.startDate).toLocaleDateString(undefined, { month: "short", day: "2-digit" })} –{" "}
                      {new Date(rule.endDate).toLocaleDateString(undefined, { month: "short", day: "2-digit" })}
                    </>
                  ) : (
                    <>
                      {new Date(rule.startDate).toLocaleDateString()} – {new Date(rule.endDate).toLocaleDateString()}
                    </>
                  )}
                </span>
              </div>

              {renderSeasonBadge(rule)}
            </div>
          </Col>

          <Col xs={12} md={4} lg={3} className="mt-2 mt-md-0">
            <div className="d-flex gap-2 justify-content-start justify-content-md-end">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  const s = String(rule.season || "").toUpperCase();
                  setDrawerMode(s === "FIXED" ? "FIXED" : (s === "PERCENTAGE" ? "PERCENTAGE" : "SEASON"));
                  setEditingSeason(rule);
                  setShowDrawer(true);
                }}
              >
                Edit
              </Button>
              <Button variant="outline-danger" size="sm" onClick={() => confirmDelete(rule.id)}>
                Remove
              </Button>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  // SeasonTab.jsx（只展示结构统一的部分，逻辑你原样保留）
  return (
    <FormSection title="Pricing Settings">
      <Row>
        {/* 1) Season rules */}
        <Col md={12} className="section-head">
          <div>
            <div className="section-title">
              {pricingMode === "SEASON" ? "Seasonal pricing" : "Percentage pricing"}
            </div>
            <div className="section-desc">
              {pricingMode === "SEASON"
                ? "Set Medium/High season adjustments for recurring or specific dates."
                : "Set percentage adjustments for recurring or specific dates."}
            </div>
          </div>

          <div className="section-action">
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                setDrawerMode(mainMode);
                setEditingSeason(null);
                setShowDrawer(true);
              }}
            >
              + {pricingMode === "SEASON" ? "Add season period" : "Add price adjustment"}
            </Button>
          </div>
        </Col>

        <Col md={12} className="mt-3">
          {seasonRules.length === 0 ? (
            <Card className="schedule-card shadow-sm border-0">
              <Card.Body className="py-3">
                <div className="text-muted small mt-1">No rules yet.</div>
              </Card.Body>
            </Card>
          ) : (
            seasonRules.map((r) => <SeasonRuleCard key={r.id ?? r.startDate + r.endDate} rule={r} />)
          )}
          <div className="section-desc">
            Season rules define date ranges. Percentage rules adjust product prices during that period.
          </div>
        </Col>

        {/* 2) Fixed rules */}
        <Col md={12} className="section-head mt-4">
          <div>
            <div className="section-title">Event price increase</div>
            <div className="section-desc">Fixed amount adjustment on top of base/event price.</div>
          </div>

          <div className="section-action">
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                setDrawerMode("FIXED");
                setEditingSeason(null);
                setShowDrawer(true);
              }}
            >
              + Add fixed increase
            </Button>
          </div>
        </Col>

        <Col md={12} className="mt-3">
          {fixedRules.length === 0 ? (
            <Card className="schedule-card shadow-sm border-0">
              <Card.Body className="py-3">
                <div className="text-muted small mt-1">No fixed increases yet.</div>
              </Card.Body>
            </Card>
          ) : (
            fixedRules.map((r) => <SeasonRuleCard key={r.id ?? r.startDate + r.endDate} rule={r} />)
          )}
        </Col>

        {/* Drawer */}
        <ShopSeasonDrawer
          show={showDrawer}
          onClose={() => { setShowDrawer(false); setEditingSeason(null); }}
          onSubmit={handleSave}
          initialData={editingSeason}
          mode={drawerMode}        // ✅ 现在 mode 是三态
          pricingMode={pricingMode} // ✅ 仍然传（用来显示提示/标题）
        />
      </Row>
    </FormSection>
  );

};




const ExtrasTab = ({ formik, extrasCatalog }) => {
  return (
    <FormSection title="Services & Policies">
      <ExtrasList formik={formik} extrasCatalog={extrasCatalog} />
    </FormSection>
  );
};

const FormSection = ({ title, children, button = null }) => (
  <section>
    <h5 className="pb-3">{title} {button}</h5>
    {children}
  </section>
);

export default ShopForm;